import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { createColumnHelper, SortingState } from '@tanstack/react-table';
import { keyBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  ColorsV2,
  CopyButton,
  Divider,
  Flex,
  getToken,
  Table,
  TableContainer,
  Text,
  useColumnVisibilityState,
  IconInCircle,
  spaces,
  IconNames,
  useTabContext,
} from '@electricjs/arc';

import { SearchBox, TableSearchBar } from '@common';
import getProductTypeLabel from '@/components/Assets/helpers/getProductTypeLabel';
import AssetAssignedToCell from '@/pages/Assets/AssetAssignedToCell';
import { Asset, AssetWithEnrollmentStatus } from '@/types/assets';
import TableCellFlexContainer from 'common/Table/TableCellFlexContainer';
import { AssetStatusColumn } from '@/pages/Assets/AssetStatus';
import { getAssetConditionDisplayNameMap } from '@/components/Assets/helpers/transformOptions';
import AssetsTableHeader from '@/pages/Assets/AssetsTableHeader';

import AssetsTableFiltersGroup from '@/pages/Assets/TableFilters/AssetsTableFiltersGroup';
import {
  AssetStatus,
  EmployeeLeaveStatusEnum,
  ProductTypeName,
  SortOrder,
} from '@electricjs/core_entity-client';
import {
  AssetManagementTabs,
  IsDeviceFilterOption,
  Warranty,
} from '@/pages/Assets/types';
import { LocalStorageKeys } from '@/constants/localStorageKeys';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetBulkEmployeeLeavesQuery } from '@/redux/slices/employeeApiSlice';
import { useEmployeesMap } from '@/hooks/useEmployeesMap';
import {
  getAssignee,
  getAssigneeId,
  isAssigneeHidden,
} from '@/helpers/assignee';
import formatDate from '@/helpers/formatDate';
import formatCurrency from '@/helpers/formatCurrency';
import TablePaginationContainer from 'common/TablePaginationContainer';
import TranslatedPagination from 'common/TranslatedPagination';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

const StyledTableContainer = styled(TableContainer)`
  overflow: visible; // Override the default overflow
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${getToken(ColorsV2.PRIMARY)};
  padding-left: ${spaces.space_m_rem};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  & > * {
    color: ${getToken(ColorsV2.PRIMARY)};
  }
`;

export enum AssetStatusValue {
  Active = 'active',
  Archived = 'archived',
}

type AssetsTableProps = {
  assets: Asset[];
  totalAssets: number;
  assetsCount: number;
  isFetchingAssets: boolean;
  handleExport: () => void;
  isExportingAssets: boolean;
  setCurrentPage: (page: number) => void;
  currentPage: number;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  handleAssetStatusFilter: (
    assetStatus: AssetStatus[] | ((prev: AssetStatus[]) => AssetStatus[])
  ) => void;
  assetStatus: AssetStatus[];
  handleProductTypeFilter: (
    productType:
      | ProductTypeName[]
      | ((prev: ProductTypeName[]) => ProductTypeName[])
  ) => void;
  productType: ProductTypeName[];
  handleWarrantyFilter: (
    warranty: Warranty[] | ((prev: Warranty[]) => Warranty[])
  ) => void;
  warranty: Warranty[];
  handleIsDeviceFilter: (
    isDevice:
      | IsDeviceFilterOption[]
      | ((prev: IsDeviceFilterOption[]) => IsDeviceFilterOption[])
  ) => void;
  isDevice: IsDeviceFilterOption[];
  sorting: SortingState;
  onManualSort: (orderBy: string, sortOrder: SortOrder) => void;
  pageSize: number;
  setExcludeAssigned: Dispatch<SetStateAction<boolean>>;
};

export const AssetColumnIds = {
  AssetName: 'assetName',
  CustomAssetId: 'customAssetId',
  AssetStatus: 'assetStatus',
  ProductType: 'productType',
  AssignedTo: 'assignedToEmployee',
  SiteName: 'siteName',
  SerialNumber: 'serialNumber',
  PurchaseDate: 'purchaseDate',
  PurchasePrice: 'purchasePrice',
  PurchasedFrom: 'purchasedFrom',
  Brand: 'brand',
  Model: 'model',
  Notes: 'notes',
  Condition: 'condition',
  DeviceId: 'deviceId',
} as const;

export type AssetColumnId =
  (typeof AssetColumnIds)[keyof typeof AssetColumnIds];

const ProductTypeMap: { [key in ProductTypeName]?: IconNames } = {
  [ProductTypeName.Accessory]: 'dongle',
  [ProductTypeName.Audio]: 'audio',
  [ProductTypeName.Cable]: 'cord',
  [ProductTypeName.Desktop]: 'desktop',
  [ProductTypeName.Dock]: 'dongle',
  [ProductTypeName.Headphones]: 'audio',
  [ProductTypeName.Keyboard]: 'keyboard',
  [ProductTypeName.KeyboardMouse]: 'keyboard',
  [ProductTypeName.Laptop]: 'laptop',
  [ProductTypeName.Monitor]: 'monitor',
  [ProductTypeName.Mouse]: 'mouse',
  [ProductTypeName.Microphone]: 'microphone',
  [ProductTypeName.CellPhone]: 'telephone',
  [ProductTypeName.OtherPhone]: 'telephone',
  [ProductTypeName.PowerAdapter]: 'battery-bolt',
  [ProductTypeName.Service]: 'wrench',
  [ProductTypeName.Tablets]: 'tablet',
  [ProductTypeName.Video]: 'webcam',
  [ProductTypeName.Warranty]: 'shield-check',
} as const;

const columnHelper = createColumnHelper<AssetWithEnrollmentStatus>();

const AssetsTable = ({
  assets,
  totalAssets,
  isFetchingAssets,
  handleExport,
  isExportingAssets,
  setCurrentPage,
  currentPage,
  searchTerm,
  setSearchTerm,
  handleAssetStatusFilter,
  assetStatus,
  handleProductTypeFilter,
  productType,
  handleWarrantyFilter,
  warranty,
  handleIsDeviceFilter,
  isDevice,
  sorting,
  onManualSort,
  pageSize,
  setExcludeAssigned,
}: AssetsTableProps) => {
  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );
  const { t } = useTranslation(['assets', 'common']);
  const conditionDisplayNameMap = getAssetConditionDisplayNameMap(t);
  const organizationId = useGetOrganizationId();
  const { selectedId: selectedTab } = useTabContext();
  const { ithEmployeeLeave, ithDivisions } = useFlags();
  const { getEmployeeById } = useEmployeesMap();
  const isDivisionsEnabled = Boolean(ithDivisions);

  // Hidden assignees are left out so their ids never reach the leaves query.
  const employeeIds = useMemo(
    () =>
      Array.from(
        new Set(
          assets
            .filter(
              asset =>
                !isAssigneeHidden(asset, {
                  isDivisionsEnabled,
                  getEmployeeById,
                })
            )
            .map(asset => getAssigneeId(asset))
            .filter((id): id is string => !!id)
        )
      ),
    [assets, isDivisionsEnabled, getEmployeeById]
  );

  const {
    data: employeeLeavesByEmployee,
    isLoading: isLoadingLeaves,
    isError: isErrorLeaves,
  } = useGetBulkEmployeeLeavesQuery(
    ithEmployeeLeave && employeeIds.length
      ? {
          organizationId,
          employeeIds,
          // InProgress powers the on-leave icon; Scheduled is also needed so
          // it can show the upcoming-leave indicator.
          statuses: [
            EmployeeLeaveStatusEnum.InProgress,
            EmployeeLeaveStatusEnum.Scheduled,
          ],
        }
      : skipToken
  );

  // Without this, a failed bulk fetch silently renders as "no one is on
  // leave" with no signal that the data is missing rather than empty.
  useEffect(() => {
    if (isErrorLeaves) {
      window?.DD_RUM?.addError('Failed to fetch bulk employee leave status', {
        location: 'AssetsTable',
      });
    }
  }, [isErrorLeaves]);

  const columnLabels = [
    {
      id: AssetColumnIds.AssetName,
      label: t('assets:assetsPage.name'),
      show: true,
    },
    {
      id: AssetColumnIds.CustomAssetId,
      label: t('assets:assetsPage.assetID'),
      show: true,
    },
    {
      id: AssetColumnIds.ProductType,
      label: t('assets:assetsPage.productType'),
      show: true,
    },
    {
      id: AssetColumnIds.SiteName,
      label: t('assets:assetsPage.site'),
      show: true,
    },
    {
      id: AssetColumnIds.SerialNumber,
      label: t('assets:assetsPage.serialNumber'),
      show: true,
    },
    {
      id: AssetColumnIds.AssignedTo,
      label: t('assets:assetsPage.assignedTo'),
      show: true,
    },
    { id: AssetColumnIds.AssetStatus, label: t('common:status'), show: true },
    {
      id: AssetColumnIds.PurchaseDate,
      label: t('assets:assetsPage.purchaseDate'),
      show: false,
    },
    {
      id: AssetColumnIds.PurchasePrice,
      label: t('assets:assetsPage.purchasePrice'),
      show: false,
    },
    {
      id: AssetColumnIds.PurchasedFrom,
      label: t('assets:assetsPage.purchasedFrom'),
      show: false,
    },
    {
      id: AssetColumnIds.Brand,
      label: t('assets:assetsPage.brand'),
      show: false,
    },
    {
      id: AssetColumnIds.Model,
      label: t('assets:assetsPage.model'),
      show: false,
    },
    {
      id: AssetColumnIds.Notes,
      label: t('assets:assetsPage.notes'),
      show: false,
    },
    {
      id: AssetColumnIds.Condition,
      label: t('assets:assetsPage.condition'),
      show: false,
    },
    {
      id: AssetColumnIds.DeviceId,
      label: t('assets:assetsPage.deviceId'),
      show: false,
    },
  ];

  const columnsById = keyBy(columnLabels, 'id');

  const tableColumns = [
    columnHelper.accessor('name', {
      header: t('assets:assetsPage.name'),
      id: AssetColumnIds.AssetName,
      meta: {
        flex: '1.5',
        enableBackendSorting: true,
      },
      cell: data => {
        return (
          <TableCellFlexContainer>
            <IconInCircle
              iconSize="medium"
              background={ColorsV2.GRAY_LIGHTER}
              flexShrink="0"
              icon={
                ProductTypeMap[
                  data.row.original.productType.name as ProductTypeName
                ] ?? 'cog'
              }
            />
            <StyledLink to={`/assets/${data.row.original.id}`}>
              <Text variant="body-2">{data.getValue()}</Text>
            </StyledLink>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('assignedToEmployee', {
      header: t('assets:assetsPage.assignedTo'),
      id: AssetColumnIds.AssignedTo,
      meta: {
        flex: '0.8',
        enableBackendSorting: true,
      },
      cell: data => {
        const asset = data.row.original;
        const assigneeResolver = { isDivisionsEnabled, getEmployeeById };
        const employee = getAssignee(asset, assigneeResolver);

        return (
          <AssetAssignedToCell
            employee={employee}
            isRestricted={isAssigneeHidden(asset, assigneeResolver)}
            leaves={employee && employeeLeavesByEmployee?.[employee.id]}
            leavesLoading={isLoadingLeaves}
          />
        );
      },
    }),
    columnHelper.accessor('serialNumber', {
      header: t('assets:assetsPage.serialNumber'),
      id: AssetColumnIds.SerialNumber,
      enableSorting: false,
      meta: {
        flex: '0.6',
      },
      cell: data => {
        const serialNumber = data.getValue();
        return serialNumber ? (
          <TableCellFlexContainer>
            <CopyButton
              id="serial-number-copy-button"
              textToCopy={serialNumber}
              successMsg={t('assets:assetsPage.copied')}
            />
            <Text variant="body-2" textOverflow="ellipsis" overflow="hidden">
              {serialNumber}
            </Text>
          </TableCellFlexContainer>
        ) : null;
      },
    }),
    columnHelper.accessor('status', {
      header: t('common:status'),
      id: AssetColumnIds.AssetStatus,
      meta: {
        flex: '0.4',
        enableBackendSorting: true,
      },
      cell: data => {
        const status = data.getValue();

        if (!status) {
          return null;
        }

        return (
          <TableCellFlexContainer>
            <AssetStatusColumn status={status} />
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('customAssetId', {
      header: t('assets:assetsPage.assetID'),
      id: AssetColumnIds.CustomAssetId,
      meta: {
        flex: '0.4',
        enableBackendSorting: true,
      },
      cell: data => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{data.getValue()}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('productType.name', {
      header: t('assets:assetsPage.type'),
      id: AssetColumnIds.ProductType,
      meta: {
        flex: '0.4',
        enableBackendSorting: true,
      },
      cell: data => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{getProductTypeLabel(data.getValue())}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('site', {
      header: t('assets:assetsPage.site'),
      id: AssetColumnIds.SiteName,
      meta: {
        flex: '0.6',
        enableBackendSorting: true,
      },
      cell: data => {
        const site = data.getValue();

        if (!site) {
          return null;
        }

        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{site.name}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('purchaseDate', {
      header: columnsById[AssetColumnIds.PurchaseDate].label,
      id: AssetColumnIds.PurchaseDate,
      enableSorting: false,
      cell: data => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{formatDate(data.getValue())}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('purchasePrice', {
      header: columnsById[AssetColumnIds.PurchasePrice].label,
      id: AssetColumnIds.PurchasePrice,
      enableSorting: false,
      cell: data => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">
              {formatCurrency(
                data.getValue(),
                data.row.original.purchasePriceCurrencyCode
              )}
            </Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('purchasedFromUrl', {
      header: columnsById[AssetColumnIds.PurchasedFrom].label,
      id: AssetColumnIds.PurchasedFrom,
      enableSorting: false,
      cell: data => {
        return (
          <TableCellFlexContainer>
            <StyledLink to={data.getValue()} target="_blank" rel="noopener">
              <Text variant="body-2">{data.getValue()}</Text>
            </StyledLink>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('brand', {
      header: columnsById[AssetColumnIds.Brand].label,
      id: AssetColumnIds.Brand,
      enableSorting: false,
      cell: data => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{data.getValue()}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('model', {
      header: columnsById[AssetColumnIds.Model].label,
      id: AssetColumnIds.Model,
      enableSorting: false,
      cell: data => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{data.getValue()}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('notes', {
      header: columnsById[AssetColumnIds.Notes].label,
      id: AssetColumnIds.Notes,
      enableSorting: false,
      cell: data => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{data.getValue()}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('condition', {
      header: columnsById[AssetColumnIds.Condition].label,
      id: AssetColumnIds.Condition,
      enableSorting: false,
      cell: data => {
        const condition = data.getValue();
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">
              {condition
                ? (conditionDisplayNameMap[condition] ?? condition)
                : null}
            </Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('device.id', {
      header: columnsById[AssetColumnIds.DeviceId].label,
      id: AssetColumnIds.DeviceId,
      enableSorting: false,
      cell: data => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{data.getValue()}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
  ];

  // For unassigned tab, filter out the assignedToEmployee column and reorder site column to be after name column
  const getUnassignedColumns = () => {
    const filteredColumns = tableColumns.filter(
      col =>
        col.id !== AssetColumnIds.AssignedTo &&
        col.id !== AssetColumnIds.SiteName
    );

    const siteColumn = tableColumns.find(
      col => col.id === AssetColumnIds.SiteName
    );
    const [firstCol, ...restCols] = filteredColumns;
    return siteColumn ? [firstCol, siteColumn, ...restCols] : tableColumns;
  };

  // Column labels for customized columns
  const selectableColumns =
    selectedTab === AssetManagementTabs.UNASSIGNED
      ? columnLabels.filter(cl => cl.id !== AssetColumnIds.AssignedTo)
      : columnLabels;

  const [columnVisibility, setColumnVisibility] = useColumnVisibilityState({
    columns: selectableColumns,
    initialColumnsSelectedKeys: selectableColumns
      .filter(cl => cl.show)
      .map(cl => cl.id),
    persistKey:
      assets.length > 0
        ? `${LocalStorageKeys.ASSET_MANAGEMENT_TABLE_COLUMNS_VISIBILITY}-${organizationId}-${selectedTab}`
        : undefined,
  });

  const sortConfig = {
    orderBy: sorting[0]?.id ?? AssetColumnIds.AssetName,
    sortOrder: sorting[0]?.desc ? SortOrder.Desc : SortOrder.Asc,
  };

  // Total page count for results handled by BE Filters
  const totalPageCountFilteredByBackend = totalAssets
    ? Math.ceil(totalAssets / pageSize)
    : 1;

  const [displaySearchTerm, setDisplaySearchTerm] = useState(searchTerm);

  const handleSearch = (term: string) => {
    setDisplaySearchTerm(term);
    setSearchTerm(term);
  };

  return (
    <StyledTableContainer flexDirection="column" width="100%">
      <AssetsTableHeader
        totalAssets={totalAssets}
        handleExport={handleExport}
        isExportingAssets={isExportingAssets}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        columns={selectableColumns}
      />
      <Divider />
      <TableSearchBar
        search={
          <Flex minHeight="3.8rem" alignItems="center">
            <SearchBox
              placeholder={t('assets:assetsPage.searchHere')}
              searchTerm={displaySearchTerm}
              onChange={handleSearch}
            />
          </Flex>
        }
        filters={
          <AssetsTableFiltersGroup
            showTypeFilter
            showStatusFilter
            showWarrantyFilter
            showIsDeviceFilter={hasMdmEntitlement}
            assetStatus={assetStatus}
            setAssetStatus={handleAssetStatusFilter}
            productType={productType}
            setProductType={handleProductTypeFilter}
            setWarranty={handleWarrantyFilter}
            warranty={warranty}
            setExcludeAssigned={setExcludeAssigned}
            setIsDevice={handleIsDeviceFilter}
            isDevice={isDevice}
            isMultiSelect
          />
        }
      />

      <Flex width="100%">
        <Table
          columns={
            selectedTab === AssetManagementTabs.UNASSIGNED
              ? getUnassignedColumns()
              : tableColumns
          }
          data={assets}
          loading={isFetchingAssets}
          emptyStateText={t('common:userAccountsTable.noResults')}
          manualSorting
          sortConfig={sortConfig}
          onManualSort={onManualSort}
          manualPagination
          state={{
            columnVisibility,
            pagination: {
              pageIndex: currentPage,
              pageSize,
            },
          }}
        />
      </Flex>
      <TablePaginationContainer>
        <TranslatedPagination
          id="assets-table-pagination"
          pageChangeCallback={(_, nextPage) => setCurrentPage(nextPage - 1)}
          currentPage={currentPage + 1}
          totalPageCount={totalPageCountFilteredByBackend}
        />
      </TablePaginationContainer>
    </StyledTableContainer>
  );
};

export default AssetsTable;
